import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { esCamion, sumarDias, type Centavos } from '@abastece/shared';
import { estadoResultados, utilidadPorProducto, type JournalEntry, type Producto, type Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { quienLoVendeMasBarato } from '../lib/abasto.js';
import { cuentasConFiado, delCatalogo, fiadoPorAntiguedad, resumenDeVentas, tramosDe, type DineroDeAnalisis } from '../lib/analisis.js';
import { DIAS_DE_CLIENTE, lesTocaComprar, riesgoDelFiado } from '../lib/clientela.js';
import { costoConocido } from '../lib/costos.js';
import {
  DIAS_EN_JUEGO,
  DIAS_DE_HISTORIA,
  confianza,
  decisionDe,
  loQueVence,
  margenPorSubir,
  ordenarDecisiones,
  queSeAcaba,
  type Decision,
  type TipoDecision,
} from '../lib/decisiones.js';
import { agotados, muchoEnElEstante } from '../lib/estante.js';
import { DIAS_DE_PRONOSTICO } from '../lib/flujo.js';
import { etiquetaRango } from '../lib/historial.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { ventasDelPeriodo } from '../lib/libros.js';
import { preciosBajoMargen } from '../lib/margenes.js';
import { pasoSuLimite } from '../lib/porCobrar.js';
import { proyeccionDelMes } from '../lib/proyeccion.js';
import { enlaceWhatsApp } from '../lib/recibo.js';
import { seEstaAcabando, sugerirCompras } from '../lib/reponer.js';
import { textoDelMes } from '../lib/resumenDelMes.js';
import { tablero } from '../lib/vencimientos.js';
import { useControl } from '../routes/Control.js';
import { leerPorComprar } from '../routes/Reponer.js';
import { pagosEnCola } from '../routes/Transferencias.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { useDinero } from './AnalisisDinero.js';
import { Chip, Ico, type NombreIcono, type Tinte } from './Iconos.js';
import { Modal } from './Modal.js';
import { Cargando, Corta, Fila, Nada, Seccion, pct, useFechas } from './PiezasDeAnalisis.js';
import { PreciosBajoMargen } from './PreciosBajoMargen.js';

/**
 * «Hoy» in «Análisis» (the owner, 2026-10-13, of what the page could add: «let's build all of
 * it»): what to do today, what puts the most money at stake first, each opening where it is dealt
 * with; how far the figures can be believed; and the month's summary to send or print. About now,
 * so the periods are not asked. The arithmetic is `lib/decisiones.ts` and `lib/resumenDelMes.ts`.
 */

/** Each kind: its picture and colour, its title, and what the amount is. */
const DECISION: Record<TipoDecision, { icono: NombreIcono; tinte: Tinte; titulo: Clave; monto: Clave }> = {
  dinero: { icono: 'billetera', tinte: 'rosa', titulo: 'analisis.decidir.dinero', monto: 'analisis.decidir.dineroMonto' },
  reponer: { icono: 'reponer', tinte: 'ambar', titulo: 'analisis.decidir.reponer', monto: 'analisis.decidir.reponerMonto' },
  vencen: { icono: 'vencimientos', tinte: 'rosa', titulo: 'analisis.decidir.vencen', monto: 'analisis.decidir.vencenMonto' },
  limite: { icono: 'fiado', tinte: 'rosa', titulo: 'analisis.decidir.limite', monto: 'analisis.decidir.limiteMonto' },
  fiadoViejo: { icono: 'fiado', tinte: 'ambar', titulo: 'analisis.decidir.fiadoViejo', monto: 'analisis.decidir.fiadoViejoMonto' },
  faltantes: { icono: 'caja', tinte: 'rosa', titulo: 'analisis.decidir.faltantes', monto: 'analisis.decidir.faltantesMonto' },
  transferencias: { icono: 'transferencias', tinte: 'ambar', titulo: 'analisis.decidir.transferencias', monto: 'analisis.decidir.transferenciasMonto' },
  margen: { icono: 'precios', tinte: 'ambar', titulo: 'analisis.decidir.margen', monto: 'analisis.decidir.margenMonto' },
  lesToca: { icono: 'clientes', tinte: 'cielo', titulo: 'analisis.decidir.lesToca', monto: 'analisis.decidir.lesTocaMonto' },
  barato: { icono: 'proveedores', tinte: 'esmeralda', titulo: 'analisis.decidir.barato', monto: 'analisis.decidir.baratoMonto' },
  sobra: { icono: 'inventario', tinte: 'violeta', titulo: 'analisis.decidir.sobra', monto: 'analisis.decidir.sobraMonto' },
};

const filaDecision = 'flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition hover:bg-white/5';

export function VistaHoy({ hoyEs, dinero }: { hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const nombre = useNombreProducto();
  const leido = useDinero(hoyEs);
  const control = useControl(7);
  const [margen, setMargen] = useState(false);
  const [resumen, setResumen] = useState(false);
  const datos = useLiveQuery(async () => {
    // Half a year: a regular's rhythm; it holds the four weeks before something ran out, three
    // months on the shelf, and the month before last for the summary.
    const desde = sumarDias(hoyEs, -(DIAS_DE_CLIENTE - 1));
    const [ventas, primera, productos, unidades, precios, existencias, compras, reempaques, salidas, conteos, lotes, clientes, usuarios, config, cola, porComprar] =
      await Promise.all([
        ventasDelPeriodo({ desde, hasta: hoyEs }),
        db.ventas.orderBy('fecha').first(),
        db.productos.toArray(),
        db.unidades.toArray(),
        db.precios.toArray(),
        db.existencias.toArray(),
        db.compras.toArray(),
        db.reempaques.where('fecha').aboveOrEqual(desde).toArray(),
        db.salidas.where('fecha').aboveOrEqual(desde).toArray(),
        db.conteos.toArray(),
        db.lotes.toArray(),
        db.clientes.toArray(),
        db.usuarios.toArray(),
        readSettings(),
        pagosEnCola(),
        leerPorComprar(),
      ]);
    const deudores = clientes.filter((c) => (c.saldo ?? 0) > 0);
    const ids = deudores.map((c) => c.id);
    const [fiados, abonos, devoluciones, incobrables] = await Promise.all([
      db.ventas.where('clienteId').anyOf(ids).toArray(),
      db.abonos.where('clienteId').anyOf(ids).toArray(),
      db.devoluciones.where('clienteId').anyOf(ids).toArray(),
      db.incobrables.where('clienteId').anyOf(ids).toArray(),
    ]);
    return {
      ventas,
      primeraVenta: primera?.fecha ?? null,
      productos,
      unidades,
      precios,
      existencias,
      compras,
      reempaques,
      salidas,
      conteos,
      lotes,
      clientes,
      usuarios,
      config,
      cola,
      // As «Por comprar» and the home screen read what is running out: a week ahead.
      acabandose: sugerirCompras({ ...porComprar, hoy: hoyEs, cobertura: 1 }).filter(seEstaAcabando),
      cuentas: cuentasConFiado({ deudores, ventas: fiados, abonos, devoluciones, incobrables }),
      riesgos: riesgoDelFiado({ deudores, ventas: fiados, abonos, devoluciones, incobrables, hoy: hoyEs }),
    };
  }, [hoyEs]);

  const calculo = useMemo(() => {
    if (!datos || !leido || !control) return null;
    const porId = new Map(datos.productos.map((p) => [p.id, p]));
    const nombreDe = (id: string): string => {
      const p = porId.get(id);
      return p ? nombre(p) : '—';
    };
    const cliente = (id: string) => datos.clientes.find((c) => c.id === id)?.nombre ?? '—';
    const usuario = (id: string) => datos.usuarios.find((u) => u.id === id)?.nombre ?? '—';
    const estante = { productos: datos.productos, existencias: datos.existencias, ventas: datos.ventas, reempaques: datos.reempaques, hoy: hoyEs, primeraVenta: datos.primeraVenta };

    const minimo = leido.pronostico.minimo;
    const fiado = fiadoPorAntiguedad(datos.cuentas, hoyEs);
    const masDe90 = fiado.tramos.find((x) => x.tramo === 'mas');
    const viejos = fiado.clientes.filter((c) => c.dias > 90).sort((a, b) => b.saldo - a.saldo);
    // A shortage past the amount set in «Configuración», as the home screen says them; one line a person.
    const faltantes = control.faltantes.filter((x) => x.monto >= datos.config.faltanteAviso);
    const porPersona = new Map<string, number>();
    for (const x of faltantes) porPersona.set(x.usuarioId, (porPersona.get(x.usuarioId) ?? 0) + x.monto);
    const faltaron = decisionDe('faltantes', [...porPersona].map(([id, monto]) => ({ nombre: usuario(id), monto })));
    const bajos = preciosBajoMargen({
      productos: datos.productos,
      unidades: datos.unidades,
      precios: datos.precios,
      existencias: datos.existencias,
      compras: datos.compras,
      minimoBp: datos.config.precioMargenMinimoBp,
    });
    const vencen = loQueVence(tablero(datos.lotes, datos.config.vencimientoAlertaDias, hoyEs), (p, u) => costoConocido(p, u, datos.existencias, datos.compras));
    const enCola = datos.cola.map((x) => x.fecha).sort()[0];

    const decisiones = ordenarDecisiones([
      minimo.saldo < 0 ? decisionDe('dinero', [{ nombre: '', monto: -minimo.saldo }], { fecha: minimo.fecha }) : null,
      decisionDe(
        'reponer',
        queSeAcaba({ agotados: agotados({ ...estante, salidas: datos.salidas, conteos: datos.conteos }), acabandose: datos.acabandose, ventas: datos.ventas, hoy: hoyEs, primeraVenta: datos.primeraVenta }).map(
          (x) => ({ nombre: nombreDe(x.productoId), monto: x.alDia * DIAS_EN_JUEGO }),
        ),
      ),
      decisionDe('vencen', vencen.map((x) => ({ nombre: nombreDe(x.productoId), monto: x.valor }))),
      decisionDe(
        'limite',
        datos.riesgos.filter((r) => pasoSuLimite({ saldo: r.debe, limite: r.limite })).map((r) => ({ nombre: r.nombre, monto: r.debe - (r.limite ?? 0) })),
      ),
      masDe90 && masDe90.monto > 0 ? { tipo: 'fiadoViejo', monto: masDe90.monto, n: masDe90.clientes, nombres: viejos.slice(0, 3).map((c) => c.nombre) } : null,
      // As many as the people it names: one cashier short three times read «Ana y 2 más» (2026-10-13).
      faltaron,
      decisionDe('transferencias', datos.cola.map((x) => ({ nombre: '', monto: x.monto })), enCola ? { fecha: enCola } : {}),
      decisionDe('margen', margenPorSubir(bajos, datos.ventas, hoyEs).map((x) => ({ nombre: nombreDe(x.productoId), monto: x.alMes }))),
      decisionDe('lesToca', lesTocaComprar(datos.ventas, hoyEs).map((x) => ({ nombre: cliente(x.clienteId), monto: x.porVez }))),
      decisionDe('barato', quienLoVendeMasBarato(datos.compras, hoyEs).map((x) => ({ nombre: nombreDe(x.productoId), monto: x.ahorroAlMes }))),
      decisionDe('sobra', muchoEnElEstante(estante).map((x) => ({ nombre: nombreDe(x.productoId), monto: x.valor }))),
    ]);

    return {
      nombreDe,
      decisiones,
      duda: confianza({ ventas: datos.ventas, productos: datos.productos, existencias: datos.existencias, conteos: datos.conteos, hoy: hoyEs, primeraVenta: datos.primeraVenta, esCamion }),
      fiado: fiado.total,
    };
  }, [datos, leido, control, hoyEs, nombre]);

  if (!datos || !leido || !calculo) return <Cargando />;
  const { nombreDe, decisiones, duda, fiado } = calculo;
  /** Under the title: the names, or what these two need said. */
  const detalle = (d: Decision): string => {
    const fecha = d.fecha ? f.corta(d.fecha) : '';
    if (d.tipo === 'dinero') return t('analisis.decidir.dineroDetalle', { n: DIAS_DE_PRONOSTICO });
    if (d.tipo === 'transferencias') return d.n === 1 ? t('analisis.decidir.transferenciasUna', { fecha }) : t('analisis.decidir.transferenciasDetalle', { n: d.n, fecha });
    return lista(d);
  };
  const lista = (d: Decision) => (d.n > d.nombres.length ? t('analisis.decidir.yMas', { lista: d.nombres.join(', '), n: d.n - d.nombres.length }) : d.nombres.join(', '));
  const dudas =
    duda.rapidas.monto > 0 ||
    duda.sinCosto.productos.length > 0 ||
    duda.negativos > 0 ||
    (duda.diasDeVentas !== null && duda.diasDeVentas < DIAS_DE_HISTORIA) ||
    leido.saldo < 0;
  const sinCosto = duda.sinCosto.productos.map((x) => nombreDe(x.productoId));
  const nunca = duda.categorias.filter((x) => x.ultima === null).map((x) => x.categoria);
  const contadas = duda.categorias.filter((x) => x.ultima !== null);

  return (
    <div className="flex flex-col gap-4" data-analisis="hoy">
      <Seccion titulo={t('analisis.decidir.titulo')} ayuda={t('analisis.decidir.ayuda')} datos="decidir">
        {decisiones.length === 0 ? (
          <Nada texto={t('analisis.decidir.nada')} />
        ) : (
          <ul className="-mx-2.5 -my-2.5 flex flex-col">
            {decisiones.map((d) => {
              const c = DECISION[d.tipo];
              // One column beside the picture: an amount in a column of its own left a 320 px phone a word a line for the title.
              const contenido = (
                <>
                  <Chip icono={c.icono} tinte={c.tinte} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium wrap-break-word">{t(c.titulo, { fecha: d.fecha ? f.conDia(d.fecha) : '' })}</span>
                    {/* A price that did not sell is still low, at nothing a month: no «C$ 0». */}
                    {d.monto > 0 && (
                      <span className="mt-0.5 block text-sm">
                        <span className="tabular font-semibold" data-decision-monto>
                          {dinero(d.monto)}
                        </span>{' '}
                        <span className="text-xs text-slate-500">{t(c.monto)}</span>
                      </span>
                    )}
                    <span className="mt-0.5 block text-xs text-slate-400 wrap-break-word">{detalle(d)}</span>
                  </span>
                  <Ico icono="adelante" className="size-4 shrink-0 self-center text-slate-500" />
                </>
              );
              return (
                <li key={d.tipo} data-decision={d.tipo} data-monto={d.monto}>
                  {d.tipo === 'margen' ? (
                    <button type="button" data-sin-icono="fila" className={filaDecision} onClick={() => setMargen(true)}>
                      {contenido}
                    </button>
                  ) : (
                    <AdondeIr tipo={d.tipo} className={filaDecision}>
                      {contenido}
                    </AdondeIr>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Seccion>

      <Seccion titulo={t('analisis.confianza.titulo')} ayuda={t('analisis.confianza.ayuda')} datos="confianza">
        <ul className="flex flex-col gap-2 text-sm text-slate-300">
          {duda.rapidas.monto > 0 && <li data-duda="rapidas">{t('analisis.confianza.rapidas', { monto: dinero(duda.rapidas.monto), n: duda.rapidas.ventas })}</li>}
          {sinCosto.length > 0 && (
            <li data-duda="sin-costo">
              {t('analisis.confianza.sinCosto', {
                n: sinCosto.length,
                lista: sinCosto.length > 3 ? t('analisis.decidir.yMas', { lista: sinCosto.slice(0, 3).join(', '), n: sinCosto.length - 3 }) : sinCosto.join(', '),
                monto: dinero(duda.sinCosto.monto),
              })}
            </li>
          )}
          {duda.negativos > 0 && (
            <li data-duda="negativos" className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0">{t('analisis.confianza.negativos', { n: duda.negativos })}</span>
              <Link to="/inventario" search={{ negativos: true }} className="btn-secundario min-h-10 gap-1.5 px-3 text-sm">
                <Ico icono="inventario" />
                {t('analisis.confianza.verNegativos')}
              </Link>
            </li>
          )}
          {duda.diasDeVentas !== null && duda.diasDeVentas < DIAS_DE_HISTORIA && datos.primeraVenta && (
            <li data-duda="pocos-dias">{t('analisis.confianza.pocosDias', { fecha: f.conDia(datos.primeraVenta), n: duda.diasDeVentas })}</li>
          )}
          {leido.saldo < 0 && <li data-duda="dinero">{t('analisis.confianza.dinero')}</li>}
          {!dudas && <li data-duda="ninguna">{t('analisis.confianza.bien')}</li>}
        </ul>
        <div className="mt-5 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-300">{t('analisis.confianza.categorias')}</h3>
            <p className="text-xs text-slate-500">{t('analisis.confianza.categoriasAyuda')}</p>
          </div>
          <Link to="/inventario/conteo" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" data-confianza-contar>
            <Ico icono="contar" />
            {t('analisis.confianza.contar')}
          </Link>
        </div>
        {duda.categorias.length === 0 && <Nada texto={t('analisis.confianza.sinCategorias')} />}
        {/* Never counted, all in one sentence: a row each was a wall of «Nunca» in a shop that has not counted yet. */}
        {nunca.length > 0 && (
          <p className="mt-3 text-sm text-rose-300" data-nunca-contadas>
            {t('analisis.confianza.nuncaContadas', { n: nunca.length, lista: nunca.join(', ') })}
          </p>
        )}
        {contadas.length > 0 && (
          <Corta filas={contadas}>
            {(vistas) => (
              <ul className="mt-1 divide-y divide-slate-800">
                {vistas.map((x) => (
                  <Fila
                    key={x.categoria}
                    nombre={x.categoria}
                    a={<span>{x.categoria}</span>}
                    detalle={t('analisis.confianza.contados', { n: x.contados, total: x.productos })}
                    valor={x.dias === 0 ? t('analisis.confianza.hoyMismo') : t('analisis.confianza.hace', { n: x.dias ?? 0 })}
                    rojo={(x.dias ?? 0) > 90}
                  />
                ))}
              </ul>
            )}
          </Corta>
        )}
      </Seccion>

      <Seccion titulo={t('analisis.resumen.titulo')} ayuda={t('analisis.resumen.ayuda')} datos="resumen">
        <button type="button" className="btn-secundario w-full sm:w-auto" onClick={() => setResumen(true)} data-resumen-ver>
          <Ico icono="compartir" />
          {t('analisis.resumen.ver')}
        </button>
      </Seccion>

      {margen && <PreciosBajoMargen onCerrar={() => setMargen(false)} />}
      {resumen && (
        <ResumenDelMes
          hoyEs={hoyEs}
          dinero={dinero}
          negocio={datos.config.negocioNombre}
          ventas={datos.ventas}
          libro={leido.datos.libro}
          productos={datos.productos}
          fiado={fiado}
          dineroHoy={leido.saldo as Centavos}
          onCerrar={() => setResumen(false)}
        />
      )}
    </div>
  );
}

/**
 * Where each kind is dealt with: its own screen, or the tab of «Análisis» that has it in full. A row
 * of the list: its picture is in what it is handed, where `pnpm iconos:check` cannot see it.
 */
function AdondeIr({ tipo, className, children }: { tipo: Exclude<TipoDecision, 'margen'>; className: string; children: ReactNode }) {
  switch (tipo) {
    case 'dinero':
    case 'lesToca':
    case 'barato':
    case 'sobra': {
      const vista = ({ dinero: 'dinero', lesToca: 'clientes', barato: 'compras', sobra: 'productos' } as const)[tipo];
      return (
        <Link to="/analisis" search={{ vista }} data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
    }
    case 'reponer':
      return (
        <Link to="/reponer" data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
    case 'vencen':
      return (
        <Link to="/vencimientos" data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
    case 'limite':
    case 'fiadoViejo':
      return (
        <Link to="/fiado" data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
    case 'faltantes':
      return (
        <Link to="/control" data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
    case 'transferencias':
      return (
        <Link to="/transferencias" data-sin-icono="fila" className={className}>
          {children}
        </Link>
      );
  }
}

/**
 * The month's summary in a sheet: this month so far or last month, as it goes by WhatsApp and
 * prints, the same text the sheet shows (`textoDelMes`).
 */
function ResumenDelMes({
  hoyEs,
  dinero,
  negocio,
  ventas,
  libro,
  productos,
  fiado,
  dineroHoy,
  onCerrar,
}: {
  hoyEs: string;
  dinero: DineroDeAnalisis;
  negocio: string;
  ventas: readonly Venta[];
  libro: readonly JournalEntry[];
  productos: readonly Producto[];
  fiado: Centavos;
  dineroHoy: Centavos;
  onCerrar: () => void;
}) {
  const t = useT();
  const f = useFechas();
  const nombre = useNombreProducto();
  const locale = localeActual();
  const [cual, setCual] = useState<'mes' | 'mesPasado'>('mes');
  const texto = useMemo(() => {
    const { actual, anterior } = tramosDe(cual, hoyEs);
    const porId = new Map(productos.map((p) => [p.id, p]));
    const nombreDe = (id: string) => {
      const p = porId.get(id);
      return p ? nombre(p) : undefined;
    };
    const resultado = estadoResultados(libro, actual);
    return textoDelMes(
      {
        negocio,
        mes: f.mesLargo(actual.desde),
        rango: etiquetaRango(actual.desde, actual.hasta, hoyEs, locale),
        mesAntes: f.mesCorto(anterior.desde),
        ventas: resumenDeVentas(ventas, actual),
        antes: resumenDeVentas(ventas, anterior),
        bruta: { utilidad: resultado.utilidadBruta, margenBp: resultado.margenBrutoBp },
        gastos: resultado.totalGastos,
        quedo: resultado.utilidadNeta,
        mejores: delCatalogo(utilidadPorProducto(ventas, nombreDe, actual))
          .filter((x) => x.utilidad > 0)
          .sort((a, b) => b.utilidad - a.utilidad)
          .map((x) => ({ nombre: x.nombre, utilidad: x.utilidad })),
        hoy: cual === 'mes' ? { proyectado: proyeccionDelMes(ventas, hoyEs).proyectado, fiado, dinero: dineroHoy } : null,
      },
      t,
      dinero,
      pct,
    );
  }, [cual, hoyEs, productos, nombre, libro, negocio, f, locale, ventas, fiado, dineroHoy, t, dinero]);

  const pestana = (activa: boolean) =>
    `rounded-lg px-3 py-2 text-sm transition ${activa ? 'bg-marca-600 font-medium text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'}`;
  return (
    <Modal
      titulo={t('analisis.resumen.titulo')}
      onCerrar={onCerrar}
      pie={
        <div className="flex gap-2">
          <a className="btn-secundario min-w-0 flex-1 px-3 text-sm" href={enlaceWhatsApp(texto)} target="_blank" rel="noreferrer" data-resumen-whatsapp>
            <Ico icono="whatsapp" />
            {t('turno.enviarWhatsapp')}
          </a>
          <button type="button" className="btn-secundario min-w-0 flex-1 px-3 text-sm" onClick={() => imprimirTexto(texto)} data-resumen-imprimir>
            <Ico icono="imprimir" />
            {t('comun.imprimir')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-resumen>
        <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('analisis.periodo')}>
          {(['mes', 'mesPasado'] as const).map((p) => (
            <button key={p} type="button" aria-pressed={cual === p} data-sin-icono="pestana" data-resumen-mes={p} className={pestana(cual === p)} onClick={() => setCual(p)}>
              {t(p === 'mes' ? 'periodo.esteMes' : 'periodo.mesPasado')}
            </button>
          ))}
        </div>
        <pre className="tabular overflow-x-auto rounded-xl bg-slate-950 p-4 text-xs leading-relaxed whitespace-pre" data-resumen-texto>
          {texto}
        </pre>
        <p className="text-xs text-slate-500">{t('analisis.resumen.pdf')}</p>
      </div>
    </Modal>
  );
}
